// Libraries

import "./i18n";
import RillAuth from "@rill/auth";
import RillBody from "@rill/body";
import RillCompress from "@rill/compress";
import RillDelegate from "@rill/delegate";
import RillFlash from "@rill/flash";
import RillHelmet from "@rill/helmet";
import RillLoader from "@rill/loader";
import RillLogger from "@rill/logger";
import RillProgress from "@rill/progress";
import RillRefreshWith from "@rill/refresh-with";
import RillSession from "@rill/session";
import RillStatic from "@rill/static";
import * as Sentry from "@sentry/react";
import Rill from "rill";
import Actions from "./actions";
import { initChunkReload } from "./chunk-reload";
import { sentryToken } from "./constants";
import { AppleAppSiteAssociation } from "./deep-linking/AppleAssociation";
import Load from "./load";
import Middleware from "./middleware";
import AppVersion from "./middleware/app-version";
import CacheBuster from "./middleware/cache-buster";
import HealthCheck from "./middleware/health-check";
import QueryParser from "./middleware/query-parser";
import TimezoneHelper from "./middleware/time-zone-helper";
import RillFetcher from "./rill/RillFetcher";
import RillReact from "./rill/RillReact";
import { createSessionStore } from "./session-store";
import castJSON from "./utils/cast-json";
import { initVersionCheck } from "./version-check";
import Views from "./views";
import Wrappers from "./wrappers";

if (process.browser) {
  initChunkReload();

  const env = process.env.NODE_ENV || "development";
  const isProdOrDev = env === "production" || env === "development";

  if (isProdOrDev) {
    Sentry.init({
      dsn: sentryToken,
      environment: env,
      enabled: true,
      integrations: [
        // PERFORMANCE FIX: Disable browserTracingIntegration - it was causing 3-9s freezes
        // by serializing large objects (1,660+ fields) on every user interaction.
        // Sentry.browserTracingIntegration(),
        // PERFORMANCE FIX: Disable replayIntegration - session replay captures too much data
        // and causes main thread blocking when serializing large state objects.
        // Sentry.replayIntegration({
        //   maskAllText: true,
        //   blockAllMedia: true,
        // }),
      ],
      // Limit max breadcrumbs to reduce memory/serialization overhead
      maxBreadcrumbs: 20,
      // Keep error tracking but disable performance tracing
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    });

    console.log(
      `[Sentry] Client-side initialized for environment: ${env} (performance integrations disabled)`
    );
  } else {
    console.log(`[Sentry] Client-side disabled for environment: ${env}`);
  }
}

const message = "My Strength Book";

const KEYV_URI =
  process.env.KEYV_URI || "mongodb://localhost:27017/rill-session";

// Initialize app. Start the timer in both environments; it is ended in the
// browser once the client app is listening (see `app.listen` below).
console.time(message);
if (!process.browser) {
  console.log("Running in environment (NODE_ENV): ", process.env.NODE_ENV);
}

const baseFetchUrl = process.browser
  ? window.env?.REACT_APP_API_URL
  : process.env.API_URL;

const app = (module.exports = Rill())
  .use(RillCompress(), TimezoneHelper, AppVersion, QueryParser)
  .use(
    RillHelmet(),
    RillStatic("dist/client", { gzip: true, cache: 6.048e8 }),
    RillBody({ transformField: castJSON }),
    RillFetcher({ name: "api", base: `${baseFetchUrl ?? ""}/api/v1/` })
  )
  .get("/health-check", HealthCheck)
  .use(
    RillSession({
      cache: !process.browser && {
        uri: KEYV_URI,
        store: createSessionStore(KEYV_URI),
      },
      keyvTtl: 1000 * 60 * 60 * 24 * 14, // 14 days
    }),
    RillAuth(),
    RillFlash(),
    RillDelegate(),
    RillRefreshWith(),
    RillLogger(),
    RillReact(),
    RillProgress({ color: "#5bc500" }),
    CacheBuster,
    RillLoader()
  )
  .setup(Load, AppleAppSiteAssociation, Middleware, Actions, Wrappers, Views);

if (process.browser) {
  app.listen(() => console.timeEnd(message));
  initVersionCheck();
}
